import type { Fee } from "src/types";

export interface WithdrawTransactionFee {
  withdraw?: Fee;
  transaction?: Fee;
}

/**
 * Compute fee for a single fee rule (percentage or flat).
 */
export function calcFeeAmount(
  amount: number,
  fee: Fee | null | undefined
): number {
  if (!fee) return 0;
  if (fee.type === "percentage") return (amount * fee.value) / 100;
  return fee.value;
}

/**
 * Total fee (withdraw + transaction) for a given amount.
 * @param decimals - decimal places for result (0 = integer, 2 = two decimals)
 */
export function calcTotalFee({
  amount,
  fee,
  decimals = 0,
}: {
  amount: number;
  fee: WithdrawTransactionFee;
  decimals?: number;
}): number {
  const withdraw = calcFeeAmount(amount, fee.withdraw);
  const transaction = calcFeeAmount(amount, fee.transaction);
  const sum = withdraw + transaction;
  if (decimals <= 0) return Math.round(sum);
  const factor = 10 ** decimals;
  return Math.round(sum * factor) / factor;
}

/**
 * Amount to withdraw so that amount + fee = balance.
 * @param decimals - decimal places for result (0 = integer, 2 = two decimals)
 */
export function amountAfterFeeEqualsBalance({
  balance,
  fee,
  decimals = 0,
}: {
  balance: number;
  fee: WithdrawTransactionFee;
  decimals?: number;
}): number {
  const w = fee.withdraw;
  const t = fee.transaction;
  const wPct = w?.type === "percentage" ? w.value / 100 : 0;
  const tPct = t?.type === "percentage" ? t.value / 100 : 0;
  const wFlat = w?.type === "flat" ? w.value : 0;
  const tFlat = t?.type === "flat" ? t.value : 0;
  const totalPct = wPct + tPct;
  const totalFlat = wFlat + tFlat;

  let result: number;
  if (totalPct > 0 && totalFlat > 0) {
    result = (balance - totalFlat) / (1 + totalPct);
  } else if (totalPct > 0) {
    result = balance / (1 + totalPct);
  } else {
    result = Math.max(0, balance - totalFlat);
  }

  if (decimals <= 0) return Math.floor(result);
  const factor = 10 ** decimals;
  return Math.floor(result * factor) / factor;
}
